import React, { useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { Popup } from 'semantic-ui-react';
import { XIcon } from '../../ui/icons';
import colors from '../../../css/colors';

const TaxMapAvailabilityControl = styled.button`
  align-items: center;
  background: ${colors.greyOrder};
  border: 1px solid ${colors.greyBorder};
  border-radius: 999px;
  color: #737379;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 11px;
  font-weight: 400;
  height: 24px;
  line-height: 16px;
  margin-left: 6px;
  padding: 0;
  position: relative;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  white-space: nowrap;

  .tax-map-switch-label {
    padding: 0 8px 0 24px;
    transition: padding 120ms ease;
  }

  .tax-map-switch-thumb {
    align-items: center;
    background: ${colors.white};
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.16);
    display: flex;
    height: 16px;
    justify-content: center;
    left: 3px;
    position: absolute;
    top: 3px;
    transition: left 120ms ease;
    width: 16px;
  }

  &[aria-checked='false'] .tax-map-switch-thumb {
    border: 1px solid ${colors.greyBorder};
    box-sizing: border-box;
  }

  &[aria-checked='false']:hover:not(:disabled) {
    border-color: #737379;
    color: ${colors.blackText};
  }

  &[aria-checked='true'] {
    background: ${colors.red};
    border-color: ${colors.red};
    color: ${colors.white};
  }

  &[aria-checked='true'] .tax-map-switch-label {
    padding: 0 24px 0 8px;
  }

  &[aria-checked='true'] .tax-map-switch-thumb {
    left: calc(100% - 19px);
  }

  &[aria-checked='mixed'] {
    border-color: #737379;
    color: ${colors.blackText};
  }

  &[aria-checked='mixed'] .tax-map-switch-thumb {
    border: 1px solid ${colors.greyBorder};
    box-sizing: border-box;
  }

  &:focus-visible {
    outline: 2px solid ${colors.redLight};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    .tax-map-switch-label,
    .tax-map-switch-thumb {
      transition: none;
    }
  }
`;

export default function TaxMapAvailabilityToggle({ propertyId, searches, isLoading, refreshSearchList }) {
  const [isUpdatingTaxMapAvailability, setIsUpdatingTaxMapAvailability] = useState(false);
  const eligibleTaxSearches = propertyId
    ? searches.filter(
        search =>
          Number(search.propertyId) === Number(propertyId) &&
          [20, 91, 92].includes(search.searchTypeId) &&
          search.continOrUpdateNumber == null &&
          !search.hasTaxMap
      )
    : [];
  const unavailableCount = eligibleTaxSearches.filter(search => search.taxMapNotAvailable).length;
  const taxMapAvailability =
    unavailableCount === 0 ? false : unavailableCount === eligibleTaxSearches.length ? true : 'mixed';
  const updateTaxMapAvailability = async () => {
    setIsUpdatingTaxMapAvailability(true);
    try {
      await axios.post(`/api/searches/SetTaxMapNotAvailableForProperty/${propertyId}`, {
        taxMapNotAvailable: taxMapAvailability !== true,
      });
      await refreshSearchList();
    } finally {
      setIsUpdatingTaxMapAvailability(false);
    }
  };

  if (eligibleTaxSearches.length === 0) {
    return null;
  }

  return (
    <Popup
      content={
        taxMapAvailability === 'mixed'
          ? 'Some original tax searches are marked N/A. Click to mark all applicable originals for this property.'
          : taxMapAvailability
          ? 'All applicable original tax searches for this property are marked N/A and excluded from the Tax Map Report. Click to undo.'
          : 'This excludes all applicable original tax searches for this property from the Tax Map Report.'
      }
      trigger={
        <TaxMapAvailabilityControl
          type="button"
          role="checkbox"
          aria-label="Map N/A for this property"
          aria-checked={taxMapAvailability}
          aria-busy={isUpdatingTaxMapAvailability}
          disabled={isLoading || isUpdatingTaxMapAvailability}
          onClick={updateTaxMapAvailability}
        >
          <span className="tax-map-switch-thumb" aria-hidden="true">
            {taxMapAvailability === true && <XIcon stroke={colors.red} focusable="false" />}
            {taxMapAvailability === 'mixed' && '−'}
          </span>
          <span className="tax-map-switch-label">
            {taxMapAvailability === 'mixed'
              ? 'Some marked N/A'
              : taxMapAvailability
              ? 'Map N/A'
              : 'Map N/A?'}
          </span>
        </TaxMapAvailabilityControl>
      }
    />
  );
}
